import { Badge, Group, Stack, Text } from '@mantine/core';
import { StaticDatasetUsage, StaticDatasetUsageReport } from '@/types/datasets.ts';

function usageLabels(item?: StaticDatasetUsage, activeComplete = true) {
  return !item || item.removal_disposition === 'unknown'
    ? ['Usage unavailable']
    : [
        ...(item.uses.includes('draft') ? ['Used by saved draft'] : []),
        ...(item.uses.includes('published') ? ['Used by published'] : []),
        ...(item.uses.includes('outstanding') ? ['Needed by pending work'] : []),
        ...(item.references.some((ref) => ref.kind === 'draft' && ref.scope === 'local_subflow')
          ? ['Referenced in saved subflow']
          : []),
        ...(item.uses.length === 0 && activeComplete ? ['No current references'] : []),
        ...(!activeComplete ? ['Usage partially checked'] : []),
      ];
}

export function StaticDatasetUsageSummary({
  item,
  report,
  loading,
}: {
  item?: StaticDatasetUsage;
  report?: StaticDatasetUsageReport;
  loading: boolean;
}) {
  return (
    <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
      {loading ? 'Checking usage…' : usageLabels(item, report?.active_complete).join(' · ')}
    </Text>
  );
}

export function StaticDatasetUsageLabels({
  item,
  activeComplete,
}: {
  item?: StaticDatasetUsage;
  activeComplete?: boolean;
}) {
  const labels = usageLabels(item, activeComplete);
  return (
    <Group gap={4}>
      {labels.map((label) => (
        <Badge key={label} color="gray" variant="light" size="sm">
          {label}
        </Badge>
      ))}
    </Group>
  );
}

export default function StaticDatasetUsageDetails({
  report,
  item,
}: {
  report?: StaticDatasetUsageReport;
  item?: StaticDatasetUsage;
}) {
  return (
    <Stack gap="xs">
      <StaticDatasetUsageLabels item={item} activeComplete={report?.active_complete} />
      {item?.uses.includes('draft') && (
        <Text size="sm">
          Remove or rebind the draft inputs, including saved subflows, before removing this source.
        </Text>
      )}
      {item?.uses.includes('published') && (
        <Text size="sm">Update the published consumers before removing this source.</Text>
      )}
      {item?.uses.includes('outstanding') && (
        <Text size="sm">
          Pending work or a saved release candidate still needs this source. Finish or retire that
          work first.
        </Text>
      )}

      {item?.references.map((ref, index) => (
        <Text size="sm" key={index} style={{ overflowWrap: 'anywhere' }}>
          {ref.kind}
          {ref.subflow_id ? ` · saved subflow ${ref.subflow_id}` : ''}
          {ref.path ? ` · ${ref.path}` : ''}
          {ref.version_id ? ` · version ${ref.version_id}` : ''}
        </Text>
      ))}
      {report && (
        <>
          {!report.active_complete && (
            <Text size="sm">
              Active usage could not be fully inspected. Removal is unavailable.
            </Text>
          )}
          <Text size="sm">{report.history_notice}</Text>
          {!report.history_complete && (
            <Text size="sm" c="dimmed">
              Historical inspection is incomplete. More versions may reference this source.
            </Text>
          )}
          <Text size="xs" c="dimmed">
            Checked {new Date(report.observed_at).toLocaleString()}
          </Text>
        </>
      )}
    </Stack>
  );
}
